/**
 * OEM Undercoat ATC — shared native undercoat auto-add (Tasks 12c/12d).
 *
 * ONE implementation of the undercoat add-to-cart merge, used by BOTH:
 *  - the alt OEM PDP (sections/mcc-main-product-alt.liquid → assets/oem-pdp-blocks.js
 *    owns the populate half and calls window.OEMUndercoatATC.attach/matchUndercoatVariant), and
 *  - the classic pre-made-oem PDP (sections/mcc-main-product.liquid → this file's
 *    initPreMadeOem() natively replaces the retired <mcc-pre-made-oem-color> element).
 *
 * mcc.js is RETIRED (never loaded). Semantics replicated from it:
 *  - MCCProductForm.onSubmitHandler (mcc.js ~:1742) + addUndercoatToCart (~:1876):
 *    undercoat line carries properties['Undercoat For'] and the MAIN form quantity.
 *  - MCCPreMadeOemColor.updateOptions (~:868) reads variant-selects selects/fieldsets
 *    positionally; updateUndercoatId (~:892-905) picks the undercoat variant whose
 *    every selectedOptions[i].value equals the main selection at the same index.
 *  - Unlike mcc.js's two sequential POSTs, we send ONE batched /cart/add.js items[]
 *    request (avoids the historical duplicate-cart bug).
 *
 * Pure logic is exported for node --test (tests/oem-pdp-undercoat.test.mjs); every
 * DOM/bootstrap touch is gated on isBrowser (same pattern as oem-search-engine.js).
 */
(function () {
  'use strict';
  var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (isBrowser && window.__OEMUndercoatATCInitialized) return;
  if (isBrowser) window.__OEMUndercoatATCInitialized = true;

  var STOREFRONT_TOKEN = '92a12a61fd6bbd940a9a841958fcb365';
  var STOREFRONT_API = 'https://dipyourcar-com.myshopify.com/api/2025-07/graphql.json';

  /* ═══════════════════ Pure logic (node-testable) ═══════════════════ */

  /* Collect Shopify line-item properties[...] from a flat [{name,value}] snapshot. */
  function collectLineItemProperties(inputs) {
    var props = {};
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].name || '';
      var m = /^properties\[(.+)\]$/.exec(name);
      if (!m) continue;
      var val = inputs[i].value;
      if (val == null || String(val).trim() === '') continue;
      props[m[1]] = val;
    }
    return props;
  }

  function inputValueByName(inputs, name) {
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === name) return inputs[i].value;
    }
    return '';
  }

  /* Undercoat VARIANT-resolution rule — exact replica of mcc.js
   * MCCPreMadeOemColor.updateUndercoatId (mcc.js:892-905): pick the first undercoat
   * variant where EVERY selectedOptions[i].value positionally equals mainOptions[i].
   * (Iterates the UNDERCOAT's own options: extra main options are ignored; an
   * undercoat option with no matching main value at that index → no match.)
   * variants: [{ id, title, price, selectedOptions: [{name, value}] }] (Storefront shape).
   * Returns the variant or null. */
  function matchUndercoatVariant(mainOptions, variants) {
    if (!variants || !variants.length) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opts = v.selectedOptions || [];
      var ok = opts.length > 0;
      for (var j = 0; j < opts.length; j++) {
        if (mainOptions[j] !== opts[j].value) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* Decide whether to intercept the main ATC submit, and build the batched
   * /cart/add.js items[]. Returns { intercept:false } or { intercept:true, items:[2] }.
   * Undercoat quantity MATCHES the main quantity — mcc.js semantics: both
   * addToCartWithVariant (~:269) and addUndercoatToCart (~:1896) used the main form
   * quantity (per-unit finish chemistry: 2x paint → 2x undercoat). */
  function planUndercoatAdd(mainInputs, undercoatInputs) {
    var undercoatId = String(inputValueByName(undercoatInputs, 'id') || '').trim();
    if (!undercoatId) return { intercept: false };  /* undercoat not populated → native */
    var mainId = String(inputValueByName(mainInputs, 'id') || '').trim();
    if (!mainId) return { intercept: false };        /* no main variant → let native handle/err */
    var qty = parseInt(inputValueByName(mainInputs, 'quantity'), 10);
    if (!(qty > 0)) qty = 1;
    return {
      intercept: true,
      items: [
        { id: mainId, quantity: qty, properties: collectLineItemProperties(mainInputs) },
        { id: undercoatId, quantity: qty, properties: collectLineItemProperties(undercoatInputs) }
      ]
    };
  }

  function snapshotInputs(form) {
    if (!form || typeof form.querySelectorAll !== 'function') return [];
    return Array.prototype.map.call(
      form.querySelectorAll('input, select, textarea'),
      function (el) { return { name: el.name, value: el.value }; }
    );
  }

  /* Factory: submit handler with injected deps (unit-testable). */
  function createUndercoatSubmitHandler(deps) {
    var getUndercoatForm = deps.getUndercoatForm;
    var fetchImpl = deps.fetchImpl;
    var getSections = deps.getSections || function () { return ''; };
    var onSuccess = deps.onSuccess || function () {};
    var onFailure = deps.onFailure || function () {};

    return function handleUndercoatSubmit(event) {
      var mainForm = event.currentTarget || event.target;
      var plan = planUndercoatAdd(snapshotInputs(mainForm), snapshotInputs(getUndercoatForm()));
      if (!plan.intercept) return; /* undercoat not populated → native submit untouched */

      event.preventDefault();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      var body = { items: plan.items };
      var sections = getSections();
      if (sections) {
        body.sections = sections;
        if (deps.sectionsUrl) body.sections_url = deps.sectionsUrl;
      }

      fetchImpl('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || (res.body && res.body.status)) { onFailure(mainForm); return; }
          onSuccess(res.body, mainForm, event.submitter);
        })
        .catch(function () { onFailure(mainForm); });
    };
  }

  /* Format cents for display, preferring the theme's formatter when present. */
  function fmtCents(cents) {
    if (isBrowser && typeof window.formatPriceAmount === 'function') {
      var out = window.formatPriceAmount(cents, 'USD');
      if (out) return out;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ═══════════════════ Browser glue ═══════════════════ */

  function getCartSectionsParam() {
    var sections = ['cart-icon-bubble'];
    var cd = document.getElementById('Cart-Drawer');
    if (cd) sections.push(cd.getAttribute('data-section') || 'cart-drawer');
    return sections.join(',');
  }

  /* Refresh cart UI from returned sections + open the drawer — same pattern as the
   * theme's native AJAX adds (mcc.js addToCartWithVariant / oem-build-kit addToCart). */
  function refreshCartAndOpenDrawer(cartData, submitter) {
    var cartDrawer = document.getElementById('Cart-Drawer');
    try {
      if (cartData && cartData.sections) {
        var keys = Object.keys(cartData.sections);
        var bubbleKey = keys.filter(function (k) { return k.indexOf('cart-icon-bubble') !== -1; })[0];
        if (bubbleKey) {
          var bdoc = new DOMParser().parseFromString(cartData.sections[bubbleKey], 'text/html');
          var bel = bdoc.body && bdoc.body.firstElementChild;
          if (bel) document.querySelectorAll('.cart-count-bubble').forEach(function (b) { b.outerHTML = bel.innerHTML; });
        }
        if (cartDrawer) {
          var drawerSection = cartDrawer.getAttribute('data-section') || 'cart-drawer';
          var drawerKey = keys.filter(function (k) { return k.indexOf(drawerSection) !== -1; })[0];
          if (drawerKey) {
            var ddoc = new DOMParser().parseFromString(cartData.sections[drawerKey], 'text/html');
            var src = ddoc.querySelector('#Cart-Drawer-Details');
            var dest = document.querySelector('#Cart-Drawer-Details');
            if (src && dest) dest.innerHTML = src.innerHTML;
          }
        }
      }
    } catch (e) {
      console.warn('[oem-uc] Cart UI refresh failed (non-fatal):', e);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'oem-undercoat-atc', cartData: cartData });
    }
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      cartDrawer.show(submitter);
    } else {
      var toggle = document.querySelector('[aria-controls="Cart-Drawer"]');
      if (toggle) toggle.click();
    }
  }

  /* Never strand the customer: re-run the browser-native submit (form.submit()
   * does not re-dispatch a submit event → no re-entry). */
  function fallbackNativeSubmit(form) {
    try {
      if (!form || form.__oemUndercoatFallback) return;
      form.__oemUndercoatFallback = true;
      if (typeof form.submit === 'function') form.submit();
    } catch (e) { /* noop */ }
  }

  /* Bind the merge interceptor to every add-to-cart form. Idempotent. It stays
   * inert until form#undercoatAddToCart is populated with a variant id. */
  function attach() {
    var handler = createUndercoatSubmitHandler({
      getUndercoatForm: function () { return document.querySelector('form#undercoatAddToCart'); },
      fetchImpl: function (url, opts) { return window.fetch(url, opts); },
      getSections: getCartSectionsParam,
      sectionsUrl: window.location.pathname,
      onSuccess: function (cartData, mainForm, submitter) { refreshCartAndOpenDrawer(cartData, submitter); },
      onFailure: function (mainForm) { fallbackNativeSubmit(mainForm); }
    });

    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.__oemUndercoatBound) return;
      form.__oemUndercoatBound = true;
      /* Bubble phase: the OEM size selectors' capture-phase listeners have already
         forced the correct main variant id by the time we read it. */
      form.addEventListener('submit', handler);
    });
    console.log('[oem-uc] Undercoat cart-merge interceptor attached to', forms.length, 'form(s)');
  }

  /* ═══════ Classic pre-made-oem PDP: native populate (replaces dead
     <mcc-pre-made-oem-color>, mcc.js:632-988) ═══════ */

  /* mcc.js MCCPreMadeOemColor.updateOptions (:868-880): read variant-selects'
   * selects/fieldsets positionally (fieldset → checked radio value). */
  function getMainSelectedOptions() {
    var vs = document.querySelector('variant-selects');
    if (!vs) return [];
    return Array.prototype.map.call(vs.querySelectorAll('select, fieldset'), function (item) {
      if (item.tagName === 'SELECT') return item.value;
      var radios = item.querySelectorAll('input');
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return radios[i].value;
      }
      return undefined;
    });
  }

  function fetchUndercoatProduct(productId) {
    var query = 'query getUndercoatVariants { node(id: "gid://shopify/Product/' + productId + '") { ... on Product { id title metafield(namespace: "custom", key: "oem_image") { value } variants(first: 100) { edges { node { id title price { amount currencyCode } selectedOptions { name value } } } } } } }';
    return fetch(STOREFRONT_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var node = data && data.data && data.data.node;
        if (!node || !node.variants) return null;
        return {
          title: node.title || 'Luna Undercoat',
          image: (node.metafield && node.metafield.value) || null,
          variants: node.variants.edges.map(function (e) { return e.node; })
        };
      });
  }

  function initPreMadeOem() {
    /* The alt PDP owns its own populate (oem-pdp-blocks.js via .oem-color-confirm). */
    if (document.querySelector('.oem-color-confirm[data-undercoat-product-id]')) return;

    var host = document.querySelector('mcc-pre-made-oem-color[undercoat-product-id]');
    if (!host) return;
    var undercoatId = String(host.getAttribute('undercoat-product-id') || '').trim();
    /* Metafield must hold a numeric product id (some legacy rows hold a handle —
       the Storefront gid lookup would fail; mcc.js had the same limitation). */
    if (!/^\d+$/.test(undercoatId)) return;

    var colorName = host.getAttribute('color-name') || '';
    var colorCode = host.getAttribute('color-code') || '';
    var undercoat = null; /* { title, image, variants } */

    console.log('[oem-uc] initPreMadeOem: undercoat product ID =', undercoatId);

    /* Bind the fulfill interceptor up front — inert until the form is populated. */
    attach();

    function populate() {
      if (!undercoat) return;
      var ucForm = document.querySelector('form#undercoatAddToCart');
      if (!ucForm) return;
      var idInput = ucForm.querySelector('input[name="id"]');
      if (!idInput) return;

      var match = matchUndercoatVariant(getMainSelectedOptions(), undercoat.variants);
      if (match) {
        /* mcc.js updateUndercoatInput (:931-948): numeric variant id +
           'Undercoat For' = `${colorName} / ${colorCode}`. */
        idInput.value = String(match.id).split('/').pop();
        var forInput = ucForm.querySelector('input[name="properties[Undercoat For]"]');
        if (forInput) forInput.value = (colorName + ' / ' + colorCode).trim();
        console.log('[oem-uc] Undercoat matched:', idInput.value, match.title, '| price:', match.price && match.price.amount);
      } else {
        idInput.value = '';
        console.warn('[oem-uc] No matching undercoat variant for options:', getMainSelectedOptions());
      }
      renderNotice(match);
    }

    /* Minimal honest UI (Task 12d decision): reveal the existing server-rendered
       [data-undercoat] explainer block (mcc.js renderUndercoatInfo :907-929 — with
       its url-vs-rgb background logic implemented correctly; mcc.js's trailing
       unconditional assignment broke URL images) + fill the explicit price into
       [data-undercoat-price], and inject one line next to the ATC button so the
       promise is visible where the purchase decision happens. */
    function renderNotice(match) {
      var block = host.querySelector('[data-undercoat]');
      if (block && match) block.style.display = 'block';

      var img = host.querySelector('[data-undercoat-image]');
      if (img && undercoat.image) {
        var v = undercoat.image;
        if (v.indexOf('://') !== -1) {
          img.style.background = 'url("' + v + '") center center / cover no-repeat';
        } else if (v.indexOf('|') !== -1 || v.indexOf(',') !== -1) {
          img.style.background = 'rgb(' + v.split('|').join(',') + ') center center / cover no-repeat';
        } else {
          img.style.background = v + ' center center / cover no-repeat';
        }
      }

      var priceStr = '';
      if (match && match.price && match.price.amount) {
        priceStr = ' (+' + fmtCents(Math.round(parseFloat(match.price.amount) * 100)) + ')';
      }
      var priceSpan = host.querySelector('[data-undercoat-price]');
      if (priceSpan) priceSpan.textContent = priceStr;

      /* Owner 2026-07: surface the undercoat-inclusive price where the decision
         happens — a badge beside the top price, and combined totals + an
         "incl. undercoat" chip on each size row (the ATC note alone got
         orphaned from the price by the mobile reorder). */
      renderPriceBadges(match);

      /* One-line note next to the ATC button */
      var mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
      var buttons = mainForm ? mainForm.querySelector('.product-form-buttons') : null;
      if (!buttons) return;
      var note = document.getElementById('oem-uc-atc-note');
      if (match) {
        if (!note) {
          note = document.createElement('div');
          note.id = 'oem-uc-atc-note';
          note.className = 'oem-uc-atc-note';
          buttons.parentNode.insertBefore(note, buttons.nextSibling);
        }
        note.textContent = 'Includes Required Undercoat' + priceStr + ', added to your cart automatically.';
      } else if (note) {
        note.remove();
      }
    }

    function renderPriceBadges(match) {
      /* Badge pill beside the main price */
      var priceBlock = document.querySelector('.product-price-tax');
      var badge = document.getElementById('oem-uc-price-badge');
      if (match && priceBlock) {
        if (!badge) {
          badge = document.createElement('span');
          badge.id = 'oem-uc-price-badge';
          badge.className = 'oem-uc-price-badge';
          priceBlock.appendChild(badge);
        }
        badge.textContent = 'Includes Required Undercoat';
      } else if (!match && badge) {
        badge.remove();
      }

      /* Combined paint+undercoat price + border chip on each size row.
         The undercoat variant is matched size-for-size (same semantics as the
         cart add); rows with no matching undercoat size stay untouched. */
      var rows = document.querySelectorAll('.ov-size');
      Array.prototype.forEach.call(rows, function (row) {
        var priceEl = row.querySelector('.ov-size-price');
        if (!priceEl) return;
        var sizeVal = row.getAttribute('data-size');
        var uv = (undercoat && undercoat.variants) ? matchUndercoatVariant([sizeVal], undercoat.variants) : null;
        var chip = row.querySelector('.ov-uc-chip');
        if (!uv || !uv.price || !uv.price.amount) {
          if (chip) chip.remove();
          if (priceEl.dataset.baseCents) priceEl.textContent = fmtCents(parseInt(priceEl.dataset.baseCents, 10));
          return;
        }
        if (!priceEl.dataset.baseCents) {
          var base = (priceEl.textContent || '').replace(/[^0-9.]/g, '');
          priceEl.dataset.baseCents = String(Math.round(parseFloat(base || '0') * 100));
        }
        var total = parseInt(priceEl.dataset.baseCents, 10) + Math.round(parseFloat(uv.price.amount) * 100);
        priceEl.textContent = fmtCents(total);
        if (!chip) {
          chip = document.createElement('span');
          chip.className = 'ov-uc-chip';
          chip.textContent = 'Includes Required Undercoat';
          row.appendChild(chip);
        }
      });
    }

    fetchUndercoatProduct(undercoatId)
      .then(function (data) {
        if (!data) { console.warn('[oem-uc] Failed to load undercoat product data'); return; }
        undercoat = data;
        console.log('[oem-uc] Undercoat loaded:', undercoat.variants.length, 'variants');
        populate();
      })
      .catch(function (err) { console.warn('[oem-uc] Undercoat fetch error:', err); });

    /* Re-resolve on variant change. The OEM size selector syncs variant-selects'
       radios/selects and dispatches change on the container (oem-variant-selector
       .liquid:398-404/482-488), which mcc.js also listened to (mcc.js:650). */
    var vs = document.querySelector('variant-selects');
    if (vs) vs.addEventListener('change', function () { setTimeout(populate, 50); });
    /* Belt-and-braces: OEM size buttons (classic + alt selectors). */
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('.ov-size, .sv-size')) setTimeout(populate, 200);
    });
  }

  /* ═══════════════════ Public API + bootstrap ═══════════════════ */

  var api = {
    collectLineItemProperties: collectLineItemProperties,
    inputValueByName: inputValueByName,
    matchUndercoatVariant: matchUndercoatVariant,
    planUndercoatAdd: planUndercoatAdd,
    snapshotInputs: snapshotInputs,
    createUndercoatSubmitHandler: createUndercoatSubmitHandler,
    attach: attach,
    refreshCartAndOpenDrawer: refreshCartAndOpenDrawer,
    fallbackNativeSubmit: fallbackNativeSubmit
  };

  if (isBrowser) {
    window.OEMUndercoatATC = api;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initPreMadeOem);
    } else {
      initPreMadeOem();
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
